import React, { useMemo } from 'react';
import { BoxProps, Flex, TextV2, TooltipV2 } from '@withjoy/joykit';
import { MapMarker } from '@withjoy/joykit/icons';
import { pxToRem } from '@withjoy/joykit/theme';
import { useTranslation } from '@shared/core';
import { useLocaleContext } from '@shared/core/i18n/LocaleContext';
import { TravelDistancesBetweenPointsResponseFragment } from '@graphql/generated';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { extractTypedDistances } from '@shared/utils/formatting/distance.formatter';
import { milesToKm } from '@shared/utils/milesToMeters';

export interface DistanceFromVenueProps extends BoxProps {
  distances?: Maybe<TravelDistancesBetweenPointsResponseFragment>;
  milesToVenue?: number | null;
  /** Size variant - affects font size */
  size?: 'sm' | 'md';
  useInterUI?: boolean;
  hasIcon?: boolean;
  iconSize?: number;
  useToWording?: boolean;
  useShortWording?: boolean;
}

export const DistanceFromVenue: React.FC<DistanceFromVenueProps> = ({
  distances,
  milesToVenue,
  color = 'mono10',
  size = 'md',
  useInterUI = true,
  hasIcon = false,
  iconSize = 16,
  useToWording = false,
  useShortWording = false,
  ...props
}) => {
  const { tq } = useTranslation('sharedComponents');
  const { language } = useLocaleContext();

  const [accommodationsTravelDistanceExperimentEnabled] = useIsFeatureEnabled('accommodationsTravelDistanceExperiment');

  const formatDistance = useMemo(() => {
    const formatter = new Intl.NumberFormat(language, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    });

    return (valueInMiles: number, showKm: boolean) => {
      const value = showKm ? milesToKm(valueInMiles) : valueInMiles;
      const unitLabel = showKm ? 'km' : 'mi';
      return {
        formatted: formatter.format(value),
        unitLabel,
      };
    };
  }, [language]);

  const { formattedDistance, alternateDistanceText } = useMemo(() => {
    let miles: number | null = null;

    if (distances && accommodationsTravelDistanceExperimentEnabled) {
      const { displayDistance } = extractTypedDistances(distances?.commonDistance);
      if (displayDistance) {
        miles = displayDistance.mi;
      }
    }

    if (!miles && milesToVenue) {
      miles = milesToVenue;
    }

    if (!miles) return { formattedDistance: null, alternateDistanceText: null };

    const primary = formatDistance(miles, false);
    const alternate = formatDistance(miles, true);

    return {
      formattedDistance: primary,
      alternateDistanceText: `${alternate.formatted} ${alternate.unitLabel}`,
    };
  }, [accommodationsTravelDistanceExperimentEnabled, distances, formatDistance, milesToVenue]);

  if (!formattedDistance) return null;

  const distanceWithUnit = `${formattedDistance.formatted} ${formattedDistance.unitLabel}`;
  const displayText = useShortWording ? tq`${distanceWithUnit}` : useToWording ? tq`${distanceWithUnit} to venue` : tq`${distanceWithUnit} from venue`;

  const fontSize = props.fontSize ?? (size === 'sm' ? pxToRem(13) : pxToRem(15));
  const typographyVariant = size === 'sm' ? 'label2' : 'body1';

  const distanceText = (
    <TextV2
      color={color}
      fontSize={fontSize}
      typographyVariant={typographyVariant}
      {...(useInterUI && { fontFamily: 'Inter UI' })}
    >
      {displayText}
    </TextV2>
  );

  return (
    <Flex
      gap={2}
      alignItems="center"
      {...props}
    >
      {hasIcon && <MapMarker size={iconSize} />}
      {alternateDistanceText ? <TooltipV2 content={alternateDistanceText}>{distanceText}</TooltipV2> : distanceText}
    </Flex>
  );
};
